// ---------------------------------------------------------------------------
// Client Logos Renderer
// ---------------------------------------------------------------------------
// Marketing primitive — publishes the editor `ClientLogos` block as an
// accessible logo strip with an optional heading. Every src goes through
// imageUrl() so a `javascript:` or unknown-scheme paste cannot ride into
// the published HTML. Logos without a usable src are dropped silently —
// no broken `<img src="">` shipped to the page.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, imageUrl } from "../escape.js";

interface LogoItem {
  src?: unknown;
  alt?: unknown;
}

interface ClientLogosProps {
  logos?: unknown;
  heading?: string;
}

const SAFE_SRC_RE = /^[^"<>]+$/;

function isLogoItem(value: unknown): value is LogoItem {
  return typeof value === "object" && value !== null;
}

function normalizeLogo(item: LogoItem): { src: string; alt: string } | null {
  const rawSrc = typeof item.src === "string" ? item.src.trim() : "";
  if (!rawSrc) return null;
  if (!SAFE_SRC_RE.test(rawSrc)) return null;
  const alt = typeof item.alt === "string" ? item.alt.trim() : "";
  return { src: rawSrc, alt };
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as ClientLogosProps;

  const logosRaw = Array.isArray(p.logos) ? p.logos : [];
  const logos = logosRaw
    .filter(isLogoItem)
    .map(normalizeLogo)
    .filter((logo): logo is { src: string; alt: string } => logo !== null);

  if (logos.length === 0) {
    return "<!-- client-logos missing logos -->";
  }

  const heading = typeof p.heading === "string" ? p.heading.trim() : "";
  const headingHtml = heading
    ? `\n    <p class="ease-client-logos__heading">${text(heading)}</p>`
    : "";

  const items = logos
    .map((logo, index) => {
      const alt = logo.alt || `Client logo ${index + 1}`;
      return `      <li class="ease-client-logos__item"><img class="ease-client-logos__image" src="${imageUrl(logo.src)}" alt="${attr(alt)}" loading="lazy" decoding="async"></li>`;
    })
    .join("\n");

  return `<section class="ease-section ease-client-logos">${headingHtml}
    <ul class="ease-client-logos__list" role="list">
${items}
    </ul>
</section>`;
}

registerRenderer("client-logos", { render });
